import {
	MAIN_MENU,
	NON_MENU_ROUTES,
	ORGANIZATION_SETTINGS_MENU,
	REPORTS_SETTINGS,
	USER_TYPE,
	ACCOUNT_SETTINGS_MENU,
	FEATURES,
} from "@constants";
import ReportService from "@services/ReportService";

class User {
	constructor(userObj) {
		// Extend initial object received from API
		Object.assign(this, userObj);
		// Build initial navigation menu items and sort into types
		this.userMenu = MAIN_MENU.filter(menuItem => menuItem.dropDown);
		this.mainMenu = MAIN_MENU.filter(
			menuItem => !menuItem.dropDown && menuItem.roles.includes(this.UserTypeId),
		);
	}

	/* Verify navigation by role */
	canNavigate(path) {
		const relativePath = path && path[0] === "/" ? path : `/${path}`;

		// Coordinators can see details of officers, but should not be shown the nav menu item for the list
		if (path.includes("officers") && this.isCoordinator()) return true;

		// Check if the path has a defined set of roles
		const menuRoute = MAIN_MENU.find(menuItem => menuItem.url === relativePath);
		if (menuRoute) {
			// Check if the user is defined as View Only
			if (this.IsViewOnly) {
				return menuRoute.showForViewOnly;
			}
			return menuRoute.roles.includes(this.UserTypeId);
		}

		const nonMenuRoute = NON_MENU_ROUTES.find(route => route.url === relativePath);
		if (nonMenuRoute) {
			return nonMenuRoute.roles.includes(this.UserTypeId);
		}

		// If not defined and restricted, allow navigation
		return true;
	}

	checkMenuPermission(menu) {
		const { JobLocationApprovalRequired, ExtraDutyApprovalRequired } = this.Organization;

		return menu.filter(menuItem => {
			// Below are individual conditions that would prevent certain menu items from appearing for a given user
			// If the profile is View Only, hide it
			if (!menuItem.showForViewOnly && this.IsViewOnly) return false;
			// If the link should not be visible for the current user's role, hide it
			if (!menuItem.roles.includes(this.UserTypeId)) return false;
			// Staff Admins for any organization should see every possible navigation option
			if (this.IsStaffAdmin) return true;
			// Internal Coordinations & staff members should only see links that they are explicitly given permission to view
			if (!menuItem.showForInternalCoord && this.isLEA() && this.IsInternalCoordinator)
				return false;
			if (
				!menuItem.showForStaff &&
				this.isLEA() &&
				!this.IsStaffAdmin &&
				!this.IsInternalCoordinator
			)
				return false;
			// Staff users of external coord or agency should only see links that they are explicitly given permission to view
			if (!menuItem.showForStaff && !this.IsStaffAdmin && !this.isLEA()) return false;

			if (menuItem.url === "/admin/location" && !JobLocationApprovalRequired) return false;
			if (menuItem.url === "/admin/approvals" && !ExtraDutyApprovalRequired) return false;
			if (menuItem.url === "/clients/rates" && !this.IsRKPay) return false;
			// If none of the above conditions are met, the menu item should be shown
			return true;
		});
	}

	/* Get dropdown navigation menu items for user */
	getUserMenu() {
		let menu = this.checkMenuPermission(this.userMenu);
		// Deposit report must be excluded from Users without Connect account
		if (!this.IsRKPay) menu = menu.filter(item => item.url !== "/payments/history");
		return menu;
	}

	/* Return top navigation bar items */
	getMainMenu() {
		return this.checkMenuPermission(this.mainMenu);
	}

	/* Get tag for Navigation bar */
	getTagName() {
		const { lang } = global;
		if (this.IsViewOnly) {
			return `${lang.t("View Only")}`;
		}
		switch (this.UserTypeId) {
			case USER_TYPE.SUPER_ADMIN:
				return `${lang.t("Admin")}`;

			case USER_TYPE.CLIENT:
				return `${lang.t("Client")}`;

			case USER_TYPE.AGENCY_ADMIN:
				return `${lang.t("Agency")}`;

			case USER_TYPE.LAW_ENFORCEMENT:
				return `${lang.t("LEA")}`;

			case USER_TYPE.COORDINATOR:
				return `${lang.t("Coordinator")}`;

			default:
				return "";
		}
	}

	/* User type checks */
	isUserType(userType) {
		return this.UserTypeId === userType;
	}

	isSuperAdmin() {
		return this.isUserType(USER_TYPE.SUPER_ADMIN);
	}

	isLEA() {
		return this.isUserType(USER_TYPE.LAW_ENFORCEMENT);
	}

	isAgencyAdmin() {
		return this.isUserType(USER_TYPE.AGENCY_ADMIN);
	}

	isClient() {
		return this.isUserType(USER_TYPE.CLIENT);
	}

	isCoordinator() {
		return this.isUserType(USER_TYPE.COORDINATOR);
	}

	isInvoicingV3 = () => this.Features.some(f => f.toLowerCase() === FEATURES.INVOICING_V3);

	isInvoicingV2 = () => this.Organization.NonRKPayInvoicingEnabled;

	/* Filter report response dependant of feature flag */
	getReports = async agencyId => {
		const reportList = await this.getReportsByUserType(this, agencyId);
		const { depositReportEnabledFlag } = window;
		const reports = reportList.data;

		if (reports.Message === "Feature Flag Disabled") {
			if (!depositReportEnabledFlag) {
				REPORTS_SETTINGS.filter(
					report => report.roles.includes(this.UserTypeId) && report.type !== "deposit",
				);
			}
			return REPORTS_SETTINGS.filter(report => report.roles.includes(this.UserTypeId));
		}
		if (!depositReportEnabledFlag) {
			return reports.filter(report => report.name !== "deposit");
		}
		return reports;
	};

	/* Get report types available to user */
	getReportsByUserType = async (user, agencyId) =>
		ReportService.get({
			endpoint: "reports/getreportsbyusertype",
			queryParams: {
				organizationId: agencyId,
				userTypeId: user.Organization.OrganizationTypeId,
				allOrgReports: false,
			},
		});

	/* Get organization settings available to user */
	getOrganizationMenu() {
		const settings = ORGANIZATION_SETTINGS_MENU.filter(setting =>
			setting.roles.includes(this.UserTypeId),
		);
		if (this.IsInternalCoordinator && !this.IsStaffAdmin) {
			return settings.filter(item =>
				item.id === "assignment" ? { ...item, title: "Assignment Rules" } : false,
			);
		}
		return settings;
	}

	/* Get account settings available to user */
	getAccountMenu() {
		return ACCOUNT_SETTINGS_MENU.filter(setting => setting.roles.includes(this.UserTypeId));
	}
}

export default User;
